import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Menu, Icon } from 'antd'
import styled from 'styled-components'
import { IconFolders, IconMyDistrict } from '@edulastic/icons'
import {
  white,
  mainTextColor,
  smallDesktopWidth,
  themeColor,
  themeColorBlue,
} from '@edulastic/colors'
import { connect } from 'react-redux'
import _ from 'lodash'
import { SMART_FILTERS } from '@edulastic/constants/const/filters'
import {
  getOrgItemBanksSelector,
  getUserDistrict,
  getUserFeatures,
} from '../../../selectors/user'

const TestFiltersNav = ({
  items,
  onSelect,
  search = {},
  features,
  itemBanks,
  district,
  libraryFolderExpanded,
}) => {
  const updatedItems = useMemo(() => {
    let updatedItemsReturn = _.cloneDeep(items)

    const { enableDistrictContentFilter } = features
    const { districtId } = district

    if (
      !enableDistrictContentFilter ||
      !districtId ||
      !itemBanks.filter(({ type }) => type === 'premium' || type === 'custom')
        .length
    ) {
      updatedItemsReturn = updatedItemsReturn.filter(
        ({ filter }) => filter != SMART_FILTERS.DISTRICT_CERTIFIED_LIBRARY
      )
    }

    return libraryFolderExpanded
      ? updatedItemsReturn
      : updatedItemsReturn.slice(0, 3)
  }, [items, features, itemBanks, district, libraryFolderExpanded])

  let selected = items[0].path
  if (search.filter) {
    const childFilterMenuItems = _.flatten(items.map((v) => v.children || []))
    const allFilterMenuItems = [...childFilterMenuItems, ...items]
    const getCurrent =
      allFilterMenuItems.find((item) => item.filter === search.filter) || {}
    selected = getCurrent?.path
  }

  const iconNameToComponent = {
    folders: <IconFolders className="anticon" aria-hidden="true" />,
    myDistrictsLibrary: (
      <IconMyDistrict
        style={{
          marginRight: '1rem',
        }}
        aria-hidden="true"
      />
    ),
  }

  return (
    <Container onSelect={onSelect} selectedKeys={[selected]}>
      {updatedItems.map((item) => (
        <Item
          data-cy={item.text}
          key={item.path}
          marginBottom={
            item.children?.length ? `${item.children.length * 3}rem` : 'auto'
          }
          wrap="wrap"
        >
          {item.icon in iconNameToComponent ? (
            iconNameToComponent[item.icon]
          ) : (
            <Icon type={item.icon} aria-hidden="true" />
          )}
          <span className={item.text === 'FOLDERS' ? 'folders' : ''}>
            {item.text}
          </span>
          {item.children?.length && (
            <Container
              selectedKeys={[selected]}
              subMenu
              onSelect={onSelect}
              onClick={({ domEvent }) => {
                domEvent.stopPropagation()
                domEvent.preventDefault()
              }}
            >
              {item.children?.map((subItem) => (
                <Item isSubItem data-cy={subItem.text} key={subItem.path}>
                  {subItem.icon === 'myDistrictsLibrary' ? (
                    <IconMyDistrict
                      style={{
                        marginRight: '1rem',
                      }}
                      aria-hidden="true"
                    />
                  ) : (
                    <Icon type={subItem.icon} aria-hidden="true" />
                  )}
                  <span
                    style={{
                      fontSize: '10.5px',
                    }}
                  >
                    {subItem.text}
                  </span>
                </Item>
              ))}
            </Container>
          )}
        </Item>
      ))}
    </Container>
  )
}

TestFiltersNav.propTypes = {
  items: PropTypes.array.isRequired,
  onSelect: PropTypes.func,
}

TestFiltersNav.defaultProps = {
  onSelect: () => {},
}

const enhance = connect((state) => ({
  features: getUserFeatures(state),
  itemBanks: getOrgItemBanksSelector(state),
  district: getUserDistrict(state),
}))

export default enhance(TestFiltersNav)

const Container = styled(Menu)`
  border-right: none;
  background: transparent;
  margin-bottom: 5px;
  ${({ subMenu }) =>
    subMenu
      ? `
  margin-top: 0px!important;
  margin-left: -1.5rem!important;
  `
      : ''}
`

const Item = styled(Menu.Item)`
  color: ${mainTextColor};
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.2px;
  margin-bottom: ${({ marginBottom }) => marginBottom || 'auto'}!important;
  margin-left: ${({ isSubItem }) => (isSubItem ? '2.5rem' : 'auto')};
  flex-wrap: ${({ wrap }) => wrap || 'nowrap'};
  overflow: visible !important;

  .anticon {
    font-size: 18px;
    margin-right: 22px;
  }

  :hover {
    color: ${themeColor};

    svg {
      fill: ${themeColor};
    }
  }

  &.ant-menu-item {
    font-size: 11px;
    display: flex;
    align-items: center;
    text-transform: uppercase;
    .folders:hover {
      color: ${themeColorBlue};
    }

    @media (max-width: ${smallDesktopWidth}) {
      height: 30px;
      line-height: 30px;
      svg {
        width: 17px;
        height: 17px;
      }
    }
  }

  &.ant-menu-item-selected {
    border-left: 3px solid ${themeColor} !important;
    background-color: ${white} !important;
    color: ${themeColor};
    box-shadow: 0 3px 10px 0 rgba(0, 0, 0, 0.1);
    border-radius: 0px 10px 10px 0px;
    &:hover {
      color: ${themeColorBlue};
    }
    svg {
      fill: ${themeColor};
    }
  }
`
